import { IBackendCartEntries } from '@rbi-ctg/menu';
import { ILoyaltyTransactionOrder, ILoyaltyTransactionV2 } from 'generated/graphql-gateway';

type NestedLoyaltyTransactionOrder = ILoyaltyTransactionOrder & {
  children: NestedLoyaltyTransactionOrder[];
};

export const parseLoyaltyTransactionToCartEntry = (loyaltyTransaction: ILoyaltyTransactionV2) => {
  /**
   * Loyalty transaction orders arrive as a flat array where modifiers/sub-items
   * reference their parent via `parentReferenceId` (1-indexed position in the
   * original array, null for top-level items). Re-nests modifiers under their
   * parent's `children` so the UI can render the order hierarchy.
   */
  const nestOrders = (orders: ILoyaltyTransactionOrder[]) => {
    const parents: NestedLoyaltyTransactionOrder[] = orders.map(o => ({ ...o, children: [] }));
    const roots: NestedLoyaltyTransactionOrder[] = [];
    parents.forEach(item => {
      if (item.parentReferenceId == null) {
        roots.push(item);
      } else {
        const parentIdx = Number(item.parentReferenceId) - 1;
        parents[parentIdx]?.children.push(item);
      }
    });
    return roots;
  };

  if (!loyaltyTransaction.transactionDetails?.orders) {
    return [];
  }

  const nestedOrders = nestOrders(
    loyaltyTransaction.transactionDetails.orders as ILoyaltyTransactionOrder[]
  );

  return nestedOrders.map(order => {
    return {
      sanityId: order.productId,
      quantity: order.quantity,
      name: order.name,
      price: order.price,
      children: order.children.map(child => ({
        sanityId: child.productId,
        quantity: child.quantity,
        name: child.name,
        price: child.price,
      })),
    } as unknown as IBackendCartEntries;
  });
};

// Total value of the order including taxes and fees
// we dont have taxes yet so we return the subtotal
export const calculateTotalCents = (loyaltyTransaction: ILoyaltyTransactionV2) => {
  return calculateSubTotalCents(loyaltyTransaction);
};

// Total value of the items in the order
export const calculateSubTotalCents = (loyaltyTransaction: ILoyaltyTransactionV2) => {
  if (!loyaltyTransaction.transactionDetails?.orders) {
    return 0;
  }
  return (
    loyaltyTransaction.transactionDetails.orders.reduce(
      (acc, entry) => acc + entry.price * entry.quantity,
      0
    ) ?? 0
  );
};
